import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import {
  EmptyStateComponent,
  IAlertComponent,
  IBadgeComponent,
  IButtonDirective,
  IInputGroupDirective,
  IInputGroupInput,
  ModalService,
  ToasterService,
  IIconComponent,
} from "@front/m19-ui";
import { UntilDestroy } from "@ngneat/until-destroy";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccessLevel, DataWarehouseShare } from "@front/m19-api-client";
import { OrganizationAccountGroups } from "@front/m19-models";
import { DataWarehouseActivationModalComponent } from "@m19-board/data-warehouse/data-warehouse-activation-modal/data-warehouse-activation-modal.component";
import { toSignal } from "@angular/core/rxjs-interop";
import { DataWarehouseEditionModalComponent } from "@m19-board/data-warehouse/data-warehouse-edition-modal/data-warehouse-edition-modal.component";
import { DataWarehouseService } from "@m19-board/services/data-warehouse.service";
import { AccountSelectionService } from "@front/m19-services";
import { filter, switchMap } from "rxjs";
import { DataWarehouseListItemComponent } from "@m19-board/data-warehouse/data-warehouse-list-item/data-warehouse-list-item.component";
import { FormsModule } from "@angular/forms";

@UntilDestroy()
@Component({
  selector: "app-data-warehouse",
  templateUrl: "./data-warehouse.component.html",
  imports: [
    TranslocoDirective,
    EmptyStateComponent,
    IAlertComponent,
    IButtonDirective,
    DataWarehouseListItemComponent,
    IBadgeComponent,
    IInputGroupDirective,
    IInputGroupInput,
    FormsModule,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataWarehouseComponent {
  protected readonly dataWarehouseService = inject(DataWarehouseService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly toasterService = inject(ToasterService);

  protected readonly AccessLevel = AccessLevel;

  protected dataShares = toSignal<DataWarehouseShare[]>(this.dataWarehouseService.dataShares$);
  protected filterQuery = signal<string>("");
  protected selectedOrganization = toSignal<OrganizationAccountGroups | undefined>(
    this.accountSelectionService.selectedOrganizationAccountGroups$,
  );

  protected displayedDataShares = computed(() => {
    return (this.dataShares() ?? [])
      .filter((dataShare) => dataShare.shareName.toLowerCase().includes(this.filterQuery().toLowerCase()))
      .sort((a, b) => {
        return a.shareId! - b.shareId!;
      });
  });

  protected openActivateDataWarehouseModal() {
    this.modalService
      .openModal<void, boolean>(DataWarehouseActivationModalComponent, {
        modalTitle: this.translocoService.translate("data-warehouse-activation.title"),
      })
      .afterClosed()
      .pipe(
        filter((activated) => activated),
        switchMap(() => this.dataWarehouseService.activateDataWarehouseFreeTest()),
      )
      .subscribe();
  }

  protected openDataShareCreationModal() {
    if (this.dataShares === undefined) {
      return;
    }
    if (this.dataShares()!.length >= DataWarehouseService.SHARES_LIMIT) {
      this.toasterService.error(this.translocoService.translate("data-warehouse.maximum_share"));
      return;
    }
    this.modalService.openModal<void, void>(DataWarehouseEditionModalComponent, {
      modalTitle: this.translocoService.translate("data-warehouse-edition.title_create"),
    });
  }
}
